import React, { useState, useEffect } from "react";
import { Plus, Edit, Trash2, Save, Users, Lock } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import api from "../utils/api";
import { useAuth } from "../context/AuthContext";
import { roleLabels } from "../utils/format";

function CompanySettings() {
  const [form, setForm] = useState({
    name: "", rfc: "", address: "", phone: "", email: "",
    logo: "", tax_rate: 16, currency: "MXN",
    doc_footer: "", doc_header: ""
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/company").then((r) => setForm(r.data));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put("/company", form);
      toast.success("Configuración guardada");
    } catch { toast.error("Error al guardar"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <label className="block text-xs text-slate-500 mb-1">Nombre de la empresa</label>
          <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-500" />
        </div>
        <div>
          <label className="block text-xs text-slate-500 mb-1">RFC</label>
          <input value={form.rfc} onChange={(e) => setForm((f) => ({ ...f, rfc: e.target.value }))}
            className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-500" />
        </div>
        <div>
          <label className="block text-xs text-slate-500 mb-1">Teléfono</label>
          <input value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
            className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-500" />
        </div>
        <div>
          <label className="block text-xs text-slate-500 mb-1">Correo electrónico</label>
          <input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-500" />
        </div>
        <div>
          <label className="block text-xs text-slate-500 mb-1">Tasa de IVA por defecto (%)</label>
          <input type="number" value={form.tax_rate} onChange={(e) => setForm((f) => ({ ...f, tax_rate: Number(e.target.value) }))}
            className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-500" />
        </div>
        <div className="col-span-2">
          <label className="block text-xs text-slate-500 mb-1">Dirección</label>
          <input value={form.address} onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
            className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-500" />
        </div>
        <div className="col-span-2">
          <label className="block text-xs text-slate-500 mb-1">Frase / Slogan</label>
          <input value={form.tagline || ""} onChange={(e) => setForm((f) => ({ ...f, tagline: e.target.value }))}
            className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-500"
            placeholder="Ej: Los mejores snacks para tu mejor amigo" />
        </div>
        <div className="col-span-2">
          <label className="block text-xs text-slate-500 mb-1">URL del logo</label>
          <input value={form.logo || ""} onChange={(e) => setForm((f) => ({ ...f, logo: e.target.value }))}
            className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-500"
            placeholder="https://... (URL pública de tu imagen de logo)" />
          {form.logo && <img src={form.logo} alt="logo preview" className="mt-2 h-14 object-contain border border-slate-200 rounded p-1" />}
        </div>
        <div className="col-span-2">
          <label className="block text-xs text-slate-500 mb-1">Encabezado de documentos</label>
          <textarea value={form.doc_header} onChange={(e) => setForm((f) => ({ ...f, doc_header: e.target.value }))}
            rows={3} placeholder="Texto que aparece al inicio de cotizaciones, facturas, etc."
            className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-500" />
        </div>
        <div className="col-span-2">
          <label className="block text-xs text-slate-500 mb-1">Pie de documentos</label>
          <textarea value={form.doc_footer} onChange={(e) => setForm((f) => ({ ...f, doc_footer: e.target.value }))}
            rows={3} placeholder="Ej: Gracias por su compra. Válido por 30 días."
            className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-500" />
        </div>
        {/* Energy rates */}
        <div className="col-span-2 border-t border-slate-200 pt-4 mt-2">
          <div className="text-sm font-bold text-slate-700 mb-3">Tarifas de Energia (para calculo automatico en lotes)</div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-slate-500 mb-1">Costo gas por hora ($MXN/hora)</label>
              <input type="number" value={form.gas_cost_per_hour || ""} onChange={(e) => setForm((f) => ({ ...f, gas_cost_per_hour: parseFloat(e.target.value) || 0 }))}
                className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-500"
                placeholder="Ej: 25.50" min="0" step="0.01" data-testid="gas-rate-input" />
              <p className="text-[10px] text-slate-400 mt-0.5">Se multiplica por las horas de quemador del lote</p>
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Costo electricidad por hora ($MXN/hora)</label>
              <input type="number" value={form.electricity_cost_per_hour || ""} onChange={(e) => setForm((f) => ({ ...f, electricity_cost_per_hour: parseFloat(e.target.value) || 0 }))}
                className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-500"
                placeholder="Ej: 8.75" min="0" step="0.01" data-testid="elec-rate-input" />
              <p className="text-[10px] text-slate-400 mt-0.5">Se multiplica por las horas de turbina del lote</p>
            </div>
          </div>
        </div>
      </div>
      <div className="flex justify-end">
        <button onClick={handleSave} disabled={saving} data-testid="save-company-btn"
          className="flex items-center gap-2 px-6 py-2 bg-brand-600 hover:bg-brand-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-60">
          <Save size={15} /> {saving ? "Guardando..." : "Guardar cambios"}
        </button>
      </div>
    </div>
  );
}

const MODULE_LABELS = {
  dashboard: "Dashboard",
  ventas: "Ventas",
  surtido: "Surtido",
  compras: "Compras",
  clientes: "Clientes",
  proveedores: "Proveedores",
  productos: "Productos",
  rentabilidad: "Rentabilidad",
  fabricacion: "Fabricación",
  planeacion: "Planeación",
  inventario: "Inventario",
  recepciones: "Recepciones MP",
  transformaciones: "Transformaciones",
  listas_precios: "Listas de Precios",
  envios: "Envíos",
  importar_ventas: "Importar Ventas",
  reportes: "Reportes",
  comprobantes: "Comprobantes",
  configuracion: "Configuración",
};

const ROLE_PRESETS = {
  admin: Object.keys(MODULE_LABELS),
  vendedor: ["dashboard", "ventas", "clientes", "productos", "surtido", "listas_precios", "comprobantes"],
  auxiliar: ["dashboard", "surtido", "inventario"],
};

function UserModal({ user, onClose, onSave }) {
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "vendedor", permissions: [] });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) setForm({ ...user, password: "", permissions: user.permissions || [] });
  }, [user]);

  const handleRoleChange = (role) => {
    const preset = ROLE_PRESETS[role] || [];
    setForm(f => ({ ...f, role, permissions: role === "admin" ? Object.keys(MODULE_LABELS) : (f.permissions.length > 0 ? f.permissions : preset) }));
  };

  const togglePermission = (mod) => {
    setForm(f => ({
      ...f,
      permissions: f.permissions.includes(mod) ? f.permissions.filter(m => m !== mod) : [...f.permissions, mod]
    }));
  };

  const selectAll = () => setForm(f => ({ ...f, permissions: Object.keys(MODULE_LABELS) }));
  const selectNone = () => setForm(f => ({ ...f, permissions: [] }));
  const applyPreset = () => setForm(f => ({ ...f, permissions: ROLE_PRESETS[f.role] || [] }));

  const handleSave = async () => {
    if (!form.name || !form.email) { toast.error("Nombre y correo son requeridos"); return; }
    if (!user && !form.password) { toast.error("La contraseña es requerida"); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.role === "admin") payload.permissions = Object.keys(MODULE_LABELS);
      if (user) {
        await api.put(`/users/${user.id}`, payload);
        toast.success("Usuario actualizado");
      } else {
        await api.post("/users", payload);
        toast.success("Usuario creado");
      }
      onSave();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al guardar"); }
    finally { setSaving(false); }
  };

  const isAdmin = form.role === "admin";

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{user ? "Editar usuario" : "Nuevo usuario"}</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-slate-500 mb-1">Nombre</label>
              <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-500" data-testid="user-name-input" />
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Correo electrónico</label>
              <input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-500" data-testid="user-email-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-slate-500 mb-1">
                Contraseña {user && "(dejar en blanco para no cambiar)"}
              </label>
              <input type="password" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-500" data-testid="user-password-input" />
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Rol</label>
              <Select value={form.role} onValueChange={handleRoleChange}>
                <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Administrador</SelectItem>
                  <SelectItem value="vendedor">Vendedor</SelectItem>
                  <SelectItem value="auxiliar">Auxiliar</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {/* Permissions */}
          <div className="border border-slate-200 rounded-lg p-3" data-testid="permissions-section">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-bold text-slate-700 uppercase tracking-wide">Permisos de acceso</span>
              {!isAdmin && (
                <div className="flex gap-2">
                  <button type="button" onClick={applyPreset} className="text-[10px] text-brand-600 hover:text-brand-700 font-medium">Preset {form.role}</button>
                  <button type="button" onClick={selectAll} className="text-[10px] text-slate-500 hover:text-slate-700 font-medium">Todos</button>
                  <button type="button" onClick={selectNone} className="text-[10px] text-slate-500 hover:text-slate-700 font-medium">Ninguno</button>
                </div>
              )}
            </div>
            {isAdmin ? (
              <p className="text-xs text-slate-500 italic">El administrador tiene acceso a todos los módulos.</p>
            ) : (
              <div className="grid grid-cols-3 gap-1.5">
                {Object.entries(MODULE_LABELS).map(([key, label]) => (
                  <label key={key} className={`flex items-center gap-1.5 px-2 py-1.5 rounded text-xs cursor-pointer transition-colors ${
                    form.permissions.includes(key) ? "bg-brand-50 text-brand-700" : "bg-slate-50 text-slate-500 hover:bg-slate-100"
                  }`} data-testid={`perm-${key}`}>
                    <input type="checkbox" checked={form.permissions.includes(key)} onChange={() => togglePermission(key)}
                      className="w-3 h-3 rounded border-slate-300 text-brand-600 focus:ring-brand-500" />
                    {label}
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">Cancelar</button>
          <button onClick={handleSave} disabled={saving} data-testid="save-user-btn"
            className="px-6 py-2 bg-brand-600 hover:bg-brand-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-60">
            {saving ? "Guardando..." : "Guardar"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function UsersSettings() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const handleDelete = async (id) => {
    if (id === currentUser?.id) { toast.error("No puedes eliminar tu propio usuario"); return; }
    if (!window.confirm("¿Eliminar usuario?")) return;
    await api.delete(`/users/${id}`);
    toast.success("Usuario eliminado");
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-slate-800">Usuarios del sistema</h3>
        <button onClick={() => { setEditing(null); setShowModal(true); }} data-testid="nuevo-usuario-btn"
          className="flex items-center gap-2 bg-brand-600 hover:bg-brand-700 text-white px-3 py-1.5 rounded-lg text-sm font-medium transition-colors">
          <Plus size={14} /> Nuevo usuario
        </button>
      </div>
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm" data-testid="users-table">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              {["Nombre", "Correo", "Rol", "Permisos", "Activo", "Acciones"].map((h) => (
                <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-3 font-medium text-slate-900">{u.name}</td>
                <td className="px-4 py-3 text-slate-600">{u.email}</td>
                <td className="px-4 py-3">
                  <span className="bg-slate-100 text-slate-700 text-xs px-2 py-0.5 rounded-full capitalize">
                    {roleLabels[u.role] || u.role}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className="text-xs text-slate-500">
                    {u.role === "admin" ? "Todos" : `${(u.permissions || []).length} módulos`}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${u.active ? "bg-brand-100 text-brand-700" : "bg-slate-100 text-slate-500"}`}>
                    {u.active ? "Activo" : "Inactivo"}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-1">
                    <button onClick={() => { setEditing(u); setShowModal(true); }} className="p-1.5 hover:bg-slate-100 rounded-md transition-colors">
                      <Edit size={14} className="text-slate-500" />
                    </button>
                    <button onClick={() => handleDelete(u.id)} className="p-1.5 hover:bg-red-50 rounded-md transition-colors">
                      <Trash2 size={14} className="text-red-400" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {showModal && <UserModal user={editing} onClose={() => setShowModal(false)} onSave={() => { setShowModal(false); load(); }} />}
    </div>
  );
}

export default function Configuracion() {
  const { user } = useAuth();
  return (
    <div className="space-y-6" data-testid="configuracion-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Configuracion</h1>
        <p className="text-slate-500 text-sm mt-1">Ajustes de la empresa y del sistema</p>
      </div>

      <Tabs defaultValue="empresa">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="empresa">Empresa</TabsTrigger>
          {user?.role === "admin" && <TabsTrigger value="usuarios">Usuarios</TabsTrigger>}
          <TabsTrigger value="seguridad">Seguridad</TabsTrigger>
        </TabsList>
        <TabsContent value="empresa" className="bg-white rounded-xl border border-slate-200 p-6 mt-4 shadow-sm">
          <CompanySettings />
        </TabsContent>
        {user?.role === "admin" && (
          <TabsContent value="usuarios" className="bg-white rounded-xl border border-slate-200 p-6 mt-4 shadow-sm">
            <UsersSettings />
          </TabsContent>
        )}
        <TabsContent value="seguridad" className="bg-white rounded-xl border border-slate-200 p-6 mt-4 shadow-sm">
          <ChangePassword />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ChangePassword() {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.current_password || !form.new_password) { toast.error("Completa todos los campos"); return; }
    if (form.new_password.length < 6) { toast.error("La nueva contrasena debe tener al menos 6 caracteres"); return; }
    if (form.new_password !== form.confirm_password) { toast.error("Las contrasenas no coinciden"); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: form.current_password, new_password: form.new_password });
      toast.success("Contrasena actualizada exitosamente");
      setForm({ current_password: "", new_password: "", confirm_password: "" });
    } catch (err) { toast.error(err.response?.data?.detail || "Error al cambiar contrasena"); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-md" data-testid="change-password-section">
      <div className="flex items-center gap-2 mb-4">
        <Lock size={18} className="text-slate-600" />
        <h3 className="text-base font-bold text-slate-900">Cambiar Contrasena</h3>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-xs text-slate-500 mb-1">Contrasena actual</label>
          <input type="password" value={form.current_password} onChange={e => setForm(f => ({ ...f, current_password: e.target.value }))}
            className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-500"
            data-testid="current-password-input" autoComplete="current-password" />
        </div>
        <div>
          <label className="block text-xs text-slate-500 mb-1">Nueva contrasena</label>
          <input type="password" value={form.new_password} onChange={e => setForm(f => ({ ...f, new_password: e.target.value }))}
            className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-500"
            data-testid="new-password-input" autoComplete="new-password" />
          <span className="text-[10px] text-slate-400">Minimo 6 caracteres</span>
        </div>
        <div>
          <label className="block text-xs text-slate-500 mb-1">Confirmar nueva contrasena</label>
          <input type="password" value={form.confirm_password} onChange={e => setForm(f => ({ ...f, confirm_password: e.target.value }))}
            className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-500"
            data-testid="confirm-password-input" autoComplete="new-password" />
        </div>
        <button type="submit" disabled={saving} data-testid="change-password-btn"
          className="flex items-center gap-2 bg-brand-600 hover:bg-brand-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-60">
          <Save size={14} /> {saving ? "Guardando..." : "Cambiar contrasena"}
        </button>
      </form>
    </div>
  );
}
